import { useMemo } from 'react';
import { Heartbeat } from '@/hooks/useHeartbeats';
import { Device } from '@/hooks/useTriggerStream';
import { buildDeviceColorMap, colorForDevice } from './vizPalette';
import { makeDeviceLabel, smoothLine, windowTimeLabels } from './chartUtils';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';

interface Props {
  beats: Heartbeat[];
  devices: Device[];
  colorMap?: Map<string, string>;
}

const W = 1200;
const H = 320;
const PAD_L = 46; // room for dBm axis labels
// dBm window: -35 (excellent) at the top, -85 (barely usable) at the bottom.
const DBM_TOP = -35;
const DBM_BOT = -85;
// A measurement gap larger than this ends the line — never draw a sweeping
// connection across hours in which a sculpture was offline.
const GAP_MS = 4 * 3600 * 1000;

/**
 * Signal field: one flowing line per light sculpture tracing its WiFi signal
 * strength (RSSI) over time. Stronger signal = higher wave.
 */
export function SignalField({ beats, devices, colorMap }: Props) {
  const t = useT(visualizeContent).charts;

  const { runs, ids, timeLabels } = useMemo(() => {
    const samples = beats
      .map((b) => ({ id: b.device_id, ts: new Date(b.created_at).getTime(), dbm: b.signal }))
      .filter((s): s is { id: string; ts: number; dbm: number } =>
        Number.isFinite(s.ts) && typeof s.dbm === 'number',
      );
    if (!samples.length) {
      return {
        runs: [] as Array<{ id: string; points: Array<{ x: number; y: number }> }>,
        ids: [] as string[],
        timeLabels: [] as string[],
      };
    }

    const tMin = Math.min(...samples.map((s) => s.ts));
    const tMax = Date.now();
    const span = Math.max(1, tMax - tMin);
    const chartW = W - PAD_L;

    const yFor = (dbm: number) => {
      const clamped = Math.max(DBM_BOT, Math.min(DBM_TOP, dbm));
      return ((DBM_TOP - clamped) / (DBM_TOP - DBM_BOT)) * H;
    };

    const ids = Array.from(new Set(samples.map((s) => s.id))).sort();
    // Split each sculpture's samples into runs at measurement gaps, so offline
    // periods stay empty instead of being bridged by a diagonal line.
    const runs = ids.flatMap((id) => {
      const own = samples.filter((s) => s.id === id).sort((a, b) => a.ts - b.ts);
      const out: Array<{ id: string; points: Array<{ x: number; y: number }> }> = [];
      let current: Array<{ x: number; y: number }> = [];
      own.forEach((s, i) => {
        if (i > 0 && s.ts - own[i - 1].ts > GAP_MS && current.length) {
          out.push({ id, points: current });
          current = [];
        }
        current.push({ x: PAD_L + ((s.ts - tMin) / span) * chartW, y: yFor(s.dbm) });
      });
      if (current.length) out.push({ id, points: current });
      return out;
    });

    return { runs, ids, timeLabels: windowTimeLabels(tMin, tMax) };
  }, [beats]);

  const localColorMap = useMemo(
    () => colorMap ?? buildDeviceColorMap(ids),
    [colorMap, ids],
  );
  const color = (id: string) => localColorMap.get(id) ?? colorForDevice(id);
  const deviceLabel = makeDeviceLabel(devices, ids, t.sculptureFallback);

  const text = 'rgb(var(--site-fg) / 0.5)';
  const yTicks = [-40, -55, -70, -85];

  return (
    <figure className="w-full" aria-label={t.signalAria}>
      <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full h-auto">
        {/* Horizontal dBm gridlines */}
        {yTicks.map((dbm) => {
          const y = ((DBM_TOP - dbm) / (DBM_TOP - DBM_BOT)) * H;
          return (
            <g key={dbm}>
              <line x1={PAD_L} y1={y} x2={W} y2={y} stroke={text} strokeWidth={0.3} opacity={0.25} />
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize={9} fontFamily="ui-monospace, monospace" fill={text}>
                {dbm}
              </text>
            </g>
          );
        })}

        {runs.map((r, ri) => (
          <path
            key={`${r.id}-${ri}`}
            d={smoothLine(r.points)}
            fill="none"
            stroke={color(r.id)}
            strokeWidth={1.6}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.9}
          />
        ))}

        {timeLabels.map((lbl, i) =>
          // Skip duplicate labels (short windows can repeat the same day).
          i > 0 && lbl === timeLabels[i - 1] ? null : (
            <text
              key={i}
              x={PAD_L + ((W - PAD_L) * i) / (timeLabels.length - 1)}
              y={H + 22}
              textAnchor={i === 0 ? 'start' : i === timeLabels.length - 1 ? 'end' : 'middle'}
              fontSize={10}
              fontFamily="ui-monospace, monospace"
              fill={text}
            >
              {lbl}
            </text>
          ),
        )}

        {runs.length === 0 && (
          <text x={W / 2} y={H / 2} textAnchor="middle" fontSize={14} fontFamily="ui-monospace, monospace" fill={text}>
            {t.noActivity}
          </text>
        )}
      </svg>

      {ids.length > 0 && (
        <figcaption className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 px-2 font-mono text-[10px] uppercase text-sitefg/55">
          {ids.map((id) => (
            <span key={id} className="inline-flex items-center gap-2 whitespace-nowrap">
              <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" aria-hidden="true">
                <circle cx="5" cy="5" r="5" fill={color(id)} />
              </svg>
              {deviceLabel(id)}
            </span>
          ))}
        </figcaption>
      )}
    </figure>
  );
}
